// Static fallback data for the /workshops page.
// In production, partner orgs + case studies are fetched live from /api/case-studies
// (managed in the admin CMS). These constants are only used if the API call fails
// or the CMS is empty.

export const WORKSHOP_CATEGORIES = [
  {
    icon: 'Award',
    title: 'Leadership Development',
    tagline: 'Develop leaders who align teams, drive accountability, and create results.',
    image: '/images/executive-seminar.jpg',
    items: [
      'Executive Leadership Coaching',
      'New Manager Bootcamps',
      'Emerging Leader Programs',
      'Communication & Decision-Making Workshops',
      'Conflict Resolution & Team Dynamics',
      'Leadership Pipeline Strategy',
    ],
  },
  {
    icon: 'Users',
    title: 'Team Building & Culture Alignment',
    tagline: 'Strengthen connection, communication, and trust inside your organization.',
    image: '/images/group-exercise.jpg',
    items: [
      'Culture Alignment Workshops',
      'Team Communication & Collaboration Training',
      'High-Performing Team Frameworks',
      'Retreats & Offsite Experiences',
      'Team Identity & Values Sessions',
      'Trust & Accountability Workshops',
    ],
  },
  {
    icon: 'Rocket',
    title: 'Entrepreneurship & Business Growth',
    tagline: 'Help founders, owners, and operators build stronger, more aligned businesses.',
    image: '/images/sbdc-seminar.jpg',
    items: [
      'Business Strategy Workshops',
      'Founder & Operator Coaching',
      'Vision & Strategic Planning',
      'Sales, Marketing & Brand Workshops',
      'Operational Systems & Workflow Training',
      'Funding & Capital Readiness Workshops',
    ],
  },
  {
    icon: 'Brain',
    title: 'AI & Technology Integration',
    tagline: 'Equip your team with modern tools to operate smarter and faster.',
    image: '/images/top-tech-award.jpg',
    items: [
      'AI for Leaders Workshops',
      'AI Tools for Business Operations',
      'ChatGPT & Workflow Automation Training',
      'AI Strategy for Organizations',
      'Tech Adoption & Change Management',
      'Digital Transformation Roadmapping',
    ],
  },
  {
    icon: 'GraduationCap',
    title: 'Youth Leadership & Career Development',
    tagline: 'Prepare the next generation of leaders, professionals, and entrepreneurs.',
    image: '/images/leader-in-you.jpg',
    items: [
      'Youth Leadership Bootcamps',
      'College & Career Readiness Programs',
      'Student Entrepreneurship Workshops',
      'School District Trainings',
      'Mentorship & Mindset Coaching',
      'Public Speaking & Confidence Workshops',
    ],
  },
  {
    icon: 'ClipboardList',
    title: 'Assessments & Diagnostics',
    tagline: 'Measure alignment, leadership effectiveness, and team health with data.',
    image: '/images/disc-assessment.jpg',
    items: [
      'The WE Alignment Index™',
      'DISC Behavioral Assessments',
      'Leadership Style Profiles',
      'Team Dynamics Diagnostics',
      'Organizational Culture Audits',
      'Custom Pre/Post-Engagement Surveys',
    ],
  },
];

export const DELIVERY_OPTIONS = [
  { icon: 'MapPin', label: 'On-Site Workshops', desc: 'In-person training at your location.' },
  { icon: 'Video', label: 'Virtual Sessions', desc: 'Live, interactive remote training.' },
  { icon: 'Calendar', label: 'Multi-Day Intensives', desc: 'Deep-dive transformation programs.' },
  { icon: 'BookOpen', label: 'Custom Curriculum', desc: 'Tailored to your team and goals.' },
  { icon: 'Building2', label: 'Keynotes & Speaking', desc: 'Energizing talks for events.' },
  { icon: 'Sparkles', label: 'Hybrid Programs', desc: 'Blended in-person + virtual delivery.' },
];

export const IDEAL_CLIENTS = [
  'Executives & Senior Leadership Teams',
  'Mid-Sized Companies (50–500 employees)',
  'Government Agencies & Municipalities',
  'School Districts & Higher Education',
  'Nonprofits & Community Organizations',
  'Entrepreneurs & High-Growth Startups',
  'Workforce Development Boards',
  'Chambers of Commerce',
];

export const TESTIMONIALS = [
  {
    quote: 'Brandon brought clarity and direction to our leadership team. Our culture is completely different now.',
    name: 'Heather M.',
    role: 'Director of Operations',
    img: '/images/testimonial-heather.png',
  },
  {
    quote: 'The training was transformational. Our team finally speaks the same language and rows in the same direction.',
    name: 'Tiara J.',
    role: 'Program Director',
    img: '/images/testimonial-tiara.png',
  },
  {
    quote: 'Practical, powerful, and immediately applicable. Brandon\'s sessions changed how we lead.',
    name: 'Executive Sponsor',
    role: 'Fortune 500 Partner',
    img: null,
  },
];

export const SPEAKING_TOPICS = [
  { icon: 'Target', title: 'The WE Mentality: From "Me" to "We"', desc: 'A keynote on building aligned, accountable, high-trust teams.' },
  { icon: 'Award', title: 'The 5 Pillars of Organizational Alignment', desc: 'How leaders diagnose and fix breakdowns in communication, role clarity, and execution.' },
  { icon: 'Brain', title: 'AI for Leaders: Lead Smarter, Not Harder', desc: 'A practical workshop on integrating AI into leadership and operations.' },
  { icon: 'Users', title: 'Culture by Design, Not Default', desc: 'Architecting team culture intentionally instead of letting it happen by accident.' },
  { icon: 'Rocket', title: 'The Founder Operating System', desc: 'For entrepreneurs ready to lead with structure, vision, and clarity.' },
  { icon: 'GraduationCap', title: 'Youth Leadership & Future-Ready Mindsets', desc: 'Building confidence, ownership, and entrepreneurial thinking in students.' },
  { icon: 'Briefcase', title: 'Workforce Development for the Next Decade', desc: 'Equipping teams for modern work — leadership, AI, and adaptability.' },
  { icon: 'Mic', title: 'Communication That Builds Trust', desc: 'Practical frameworks for honest, productive, high-impact conversations.' },
];

// Partner logo grid fallback (used only if /api/case-studies is empty)
export const PARTNER_ORGS_FALLBACK = [
  { name: 'CCSD', sub: 'Clark County School District', logo: '/partners/ccsd.png' },
  { name: 'UNLV', sub: 'University of Nevada, Las Vegas', logo: '/partners/unlv.png' },
  { name: 'U.S. Air Force', sub: 'United States Air Force', logo: '/partners/us-air-force.png' },
  { name: 'City of Las Vegas', sub: 'Municipal Partner', logo: '/partners/city-las-vegas.png' },
  { name: 'VBOC', sub: 'Veterans Business Outreach Center', logo: '/partners/vboc.png' },
  { name: 'Urban Chamber', sub: 'Urban Chamber of Commerce', logo: '/partners/urban-chamber.png' },
  { name: 'NV Contractors', sub: 'Nevada Contractors Association', logo: '/partners/nv-contractors.png' },
  { name: 'Junior Achievement', sub: 'Youth Leadership Partner', logo: '/partners/junior-achievement.png' },
  { name: 'CSN', sub: 'College of Southern Nevada', logo: '/partners/csn.png' },
  { name: 'Manufacture NV', sub: 'Manufacture Nevada', logo: '/partners/manufacture-nv.png' },
];

// Case studies fallback (used only if /api/case-studies is empty)
export const CASE_STUDIES_FALLBACK = [
  {
    org: 'Clark County School District',
    short: 'CCSD',
    logo: '/partners/ccsd.png',
    title: 'Leadership Development & Student Entrepreneurship Training',
    audience: 'Students & Educators',
    description: 'Facilitated interactive entrepreneurship and leadership workshops designed to improve teamwork, communication, confidence, and business readiness.',
    outcome: 'Improved student engagement through AI-integrated entrepreneurship curriculum and DISC-based team development.',
    image: '/images/leader-in-you.jpg',
  },
];

export const CALENDLY_URL = 'https://calendly.com/coachbrandonward-1/schedule_with_cbw';
